import { apiClient } from '../client';
import type {
  ActiveSubscriptionResponse,
  CommissionBill,
  BillingDetails,
  BillingDetailsResponse,
  BillingInvoicesResponse,
} from '@/types/profile';

export interface CreateSubscriptionRequest {
  planId: number;
  startDate: string;
  endDate: string;
  userId?: number;
  isActive?: boolean;
  offer_id?: string;
  total_count?: number;
  razorpaySubscriptionId?: string;
  createdById?: number;
  updatedById?: number;
  notes?: { usage_model: string };
}

export interface CancelSubscriptionRequest { idSubscription: number, atPeriodEnd: string }

/**
 * What the server says a purchase costs.
 *
 * The `*Minor` fields are the authoritative integers the gateway is charged in;
 * `gross`/`discount`/`net` are the same amounts pre-formatted for display. Never
 * compute a price from the formatted strings, and never send one to the server.
 */
export interface CheckoutQuote {
  quoteId: string;
  planId: number;
  planName: string;
  billingCycle: string;
  currency: string;
  grossMinor: number;
  discountMinor: number;
  netMinor: number;
  gross: string;
  discount: string;
  net: string;
  coupon: { code: string; type: string; value: number } | null;
  /** Why the submitted coupon was not applied. The quote is still valid. */
  couponError: string | null;
  expiresAt: string;
}

/** What moving to another plan would do, priced by the server. */
export interface PlanChangePreview {
  changeKind: 'upgrade' | 'downgrade' | 'currency_change' | 'same';
  currentPlan: {
    id: number;
    planName: string;
    cycle: string;
    currency: string;
    amountMinor: number;
  };
  targetPlan: {
    id: number;
    planName: string;
    cycle: string;
    currency: string;
    amountMinor: number;
  };
  /** Whether the change happens now or when the paid period ends. */
  effective: 'immediate' | 'at_period_end';
  effectiveDate: string;
  payableNowMinor: number;
  payableNow: string;
  currency: string;
  currentPeriodEnd: string;
  /** Days of the current plan given up by upgrading today. */
  forfeitedDays: number;
  /** False when the new plan will have to be renewed by hand. */
  autoRenewAfterChange: boolean;
  limitChanges: Array<{ key: string; from: number | null; to: number | null }>;
  warnings: string[];
}

export interface PlanChangeResult {
  changeKind: string;
  subscriptionId: number;
  /** Present only for an upgrade — a downgrade takes no payment today. */
  redirectUrl?: string | null;
  effectiveDate?: string;
  payableAtRenewal?: string;
  currency?: string;
  message?: string;
  duplicate?: boolean;
}

export const subscriptionplanService = {
    createSubscription: async (data: CreateSubscriptionRequest): Promise<any> => {
      const { data: response } = await apiClient.post("/subscriptions", data);
      return response;
    },

    /**
     * The cycle is not sent: one plan row exists per (tier, cycle), so the plan
     * id already identifies it and the server derives the rest.
     */
    getQuote: async (planId: number, couponCode?: string): Promise<CheckoutQuote> => {
      const payload: Record<string, unknown> = { planId };
      if (couponCode) payload.couponCode = couponCode;
      const { data } = await apiClient.post<CheckoutQuote>(`/subscriptions/quote`, payload);
      return data;
    },


    /** What a move to `planId` would cost and when it would happen. */
    getPlanChangePreview: async (planId: number): Promise<PlanChangePreview> => {
      const { data } = await apiClient.get<PlanChangePreview>(
        `/subscriptions/plan-change/preview`,
        { params: { planId } },
      );
      return data;
    },

    /**
     * Starts a plan change.
     *
     * `changeRequestKey` is generated once per user action and repeated on a
     * retry, so a double submission returns the first result instead of
     * starting — and charging for — a second change.
     */
    createPlanChange: async (payload: {
      planId: number;
      quoteId?: string;
      changeRequestKey: string;
    }): Promise<PlanChangeResult> => {
      const { data } = await apiClient.post<PlanChangeResult>(
        `/subscriptions/plan-change`,
        payload,
      );
      return data;
    },

    /** Monthly Custom plan bills with their commission and GST. */
    getAdRevenueBills: async (): Promise<CommissionBill[]> => {
      const { data } = await apiClient.get<CommissionBill[]>(`/billing/ad-revenue/bills`);
      return data;
    },

    /** Asks the Sellco team to set up a Custom plan. Repeating it is harmless. */
    requestCustomPlan: async (note?: string): Promise<{ id: number; status: string }> => {
      const { data } = await apiClient.post(`/billing/ad-revenue/custom-plan-request`, { note });
      return data;
    },

    cancelScheduledPlanChange: async (): Promise<unknown> => {
      const { data } = await apiClient.delete(
        `/subscriptions/plan-change/scheduled`,
      );
      return data;
    },

   getActiveSubscriptionProfiles: async (): Promise<ActiveSubscriptionResponse> => {
     try {
       const { data } = await apiClient.get(`/subscriptions/active`);
       return data;
     } catch (error) {
       console.error("Error fetching active subscription:", error);
       throw error;
     }
   },

  getSubscriptionHistory: async (params?: {
    page?: number;
    limit?: number;
  }): Promise<import("@/types/profile").SubscriptionHistoryResponse> => {
    try {
      const cleanParams: Record<string, any> = {};
      if (params?.page != null) cleanParams.page = params.page;
      if (params?.limit != null) cleanParams.limit = params.limit;
      const { data } = await apiClient.get(`/subscriptions/history`, {
        params: Object.keys(cleanParams).length > 0 ? cleanParams : undefined,
      });
      return data;
    } catch (error) {
      console.error("Error fetching subscription history:", error);
      throw error;
    }
  },

  getPayments: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<import("@/types/profile").PaymentHistoryResponse> => {
    try {
      const cleanParams: Record<string, any> = {};
      if (params?.page != null) cleanParams.page = params.page;
      if (params?.limit != null) cleanParams.limit = params.limit;
      if (params?.status) cleanParams.status = params.status;
      const { data } = await apiClient.get(`/subscriptions/payments`, {
        params: Object.keys(cleanParams).length > 0 ? cleanParams : undefined,
      });
      return data;
    } catch (error) {
      console.error("Error fetching payment history:", error);
      throw error;
    }
  },

  getBillingDetails: async (): Promise<BillingDetailsResponse> => {
    try {
      const { data } = await apiClient.get<BillingDetailsResponse>('/billing/details');
      return data;
    } catch (error) {
      console.error('Error fetching billing details:', error);
      throw error;
    }
  },

  updateBillingDetails: async (details: BillingDetails): Promise<any> => {
    try {
      const { data } = await apiClient.put('/billing/details', details);
      return data;
    } catch (error) {
      console.error('Error updating billing details:', error);
      throw error;
    }
  },

  getInvoices: async (params?: {
    page?: number;
    limit?: number;
  }): Promise<BillingInvoicesResponse> => {
    try {
      const cleanParams: Record<string, any> = {};
      if (params?.page != null) cleanParams.page = params.page;
      if (params?.limit != null) cleanParams.limit = params.limit;
      const { data } = await apiClient.get<BillingInvoicesResponse>(`/billing/invoices`, {
        params: Object.keys(cleanParams).length > 0 ? cleanParams : undefined,
      });
      return data;
    } catch (error) {
      console.error('Error fetching invoices:', error);
      throw error;
    }
  },

  getBillingInvoices: async (params?: {
    page?: number;
    limit?: number;
  }): Promise<BillingInvoicesResponse> => {
    try {
      const cleanParams: Record<string, any> = {};
      if (params?.page != null) cleanParams.page = params.page;
      if (params?.limit != null) cleanParams.limit = params.limit;
      const { data } = await apiClient.get<BillingInvoicesResponse>(`/billing/invoices`, {
        params: Object.keys(cleanParams).length > 0 ? cleanParams : undefined,
      });
      return data;
    } catch (error) {
      console.error('Error fetching billing invoices:', error);
      throw error;
    }
  },

  downloadInvoicePdf: async (
    invoiceId: string | number,
    invoiceNumber?: string | null
  ): Promise<Blob> => {
    try {
      const url = `/billing/invoices/${invoiceId}/pdf`;
      const response = await apiClient.get(url, {
        responseType: 'blob',
      });
      const filename = `${(invoiceNumber ? invoiceNumber.replace(/\//g, '-') : `invoice-${invoiceId}`)}.pdf`;
      const blob = new Blob([response.data], { type: 'application/pdf' });
      if (typeof window !== 'undefined' && typeof window.URL?.createObjectURL === 'function') {
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(objectUrl);
      }
      return response.data;
    } catch (error) {
      console.error('Error downloading invoice PDF:', error);
      throw error;
    }
  },

  getPlanChanges: async (params?: {
    page?: number;
    limit?: number;
  }): Promise<import("@/types/profile").PlanChangeHistoryResponse> => {
    try {
      const cleanParams: Record<string, any> = {};
      if (params?.page != null) cleanParams.page = params.page;
      if (params?.limit != null) cleanParams.limit = params.limit;
      const { data } = await apiClient.get(`/subscriptions/plan-changes`, {
        params: Object.keys(cleanParams).length > 0 ? cleanParams : undefined,
      });
      return data;
    } catch (error) {
      console.error("Error fetching plan changes:", error);
      throw error;
    }
  },

   getAllSubscriptionProfiles: async (): Promise<any> => {
    try {
      const { data } = await apiClient.get(`/subscriptions`);
      return data;
    } catch (error) {
      console.error("Error fetching active subscription:", error);
      throw error;
    }
  },

  cancelSubscription: async (idSubscription: number, atPeriodEnd: boolean = true): Promise<any> => {
    try {
      const { data: response } = await apiClient.post(`/subscriptions/${idSubscription}/cancel`, {
        atPeriodEnd: atPeriodEnd.toString()
      });
      return response;
    } catch (error) {
      console.error("Error cancelling subscription:", error);
      throw error;
    }
  },

  getAllsubscriptionInvoice: async (): Promise<any> => {
    try {
      const { data } = await apiClient.get(`/subscriptions/invoice`);
      return data;
    } catch (error) {
      console.error("Error fetching all subscription invoice:", error);
      throw error;
    }
  },

  };

export const downloadInvoicePdf = subscriptionplanService.downloadInvoicePdf;
